import {
    ContentDetailInfoShell,
    ContentDetailThreeColLayout,
    DetailFilePanel,
    DetailPanel,
    DetailKvTable,
} from '../../../../components/detailLayout';
import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {useDispatch, useSelector} from "react-redux";
import Th_title from "../../../../components/Th_title";
import ReadOnlyInputBasic from "../../../../components/input/ReadOnlyInputBasic";
import {
    CodeIdType,
    CodeType,
    ContentCategory,
    ContentStatus,
    UserIdType,
    UserType
} from "../../../../constant/CodeSpecNum";
import {completePreRegisterWork, getServiceRequest} from "../../../../api/serviceRequest/ServiceRequestService";
import {skeleton} from "../../../../redux/slice/apiSlice";
import {resetContents, setApprovalProcesses} from "../../../../redux/slice/contentSlice";
import {handleActiveToast} from "../../../../components/sonner/ToastBasic";
import {changeRefreshContentStatus} from "../../../../redux/slice/statusSlice";
import {useTranslation} from "react-i18next";
import {getServiceCode} from "../../../../api/serviceCode/ServiceCodeService";
import {onCreateIdClickListener} from "../../../../components/commonmethod/simmsDrawerMethod";
import Selector from "../../../../components/selector/Selector";
import {validation} from "../serviceUtils";
import SimmsInputSearch from "../../../../components/input/SimmsInputSearch";
import SimmsDrawerSearch from "../../../../components/drawer/simms/SimmsDrawerSearch";
import SearchSelector from "../../../../components/searchSelector/SearchSelector";
import UserSelectionField from "../../../../components/selection/UserSelectionField";
import ApprovalComponent from "../../../settings/approval/new/ApprovalComponent";
import {clearApprovalProcessState} from "../../../settings/approval/shared/approvalProcessStore";

const ServiceRequestPreRegisterInfo = ({id}) => {
    const [serviceRequest, setServiceRequest] = useState({});
    const [docs, setDocs] = useState([]);

    const userAuth = useSelector(state => state.auth.user);

    const {t} = useTranslation();
    const dispatch = useDispatch();
    const skipServiceCodeFetchRef = useRef(false);
    const approvalProcessRef = useRef(null);
    const serviceRequestRef = useRef(serviceRequest);
    const docsRef = useRef(docs);

    serviceRequestRef.current = serviceRequest;
    docsRef.current = docs;

    const handleApprovalProcessSet = useCallback((approvalProcess) => {
        approvalProcessRef.current = approvalProcess ?? null;
    }, []);

    useEffect(() => {
        if (!id) return;
        skipServiceCodeFetchRef.current = true;
        setServiceRequest({});
        setDocs([]);
        handleCallServiceRequest();
        dispatch(skeleton(false));
        dispatch(resetContents());
        return () => dispatch(resetContents());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    useEffect(() => {
        const serviceCodeId = serviceRequest.serviceCodeId;
        if (!serviceCodeId) {
            return;
        }

        if (skipServiceCodeFetchRef.current) {
            skipServiceCodeFetchRef.current = false;
            return;
        }

        void handleSetData(serviceCodeId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [serviceRequest.serviceCodeId]);

    const handleCallServiceRequest = async () => {
        const response = await getServiceRequest({id, progressStatus: ContentStatus.PRE_REGISTER.statusValue});
        if (response && response.status === 200) {
            const data = response.data;
            const serviceCode = data.serviceCode;
            const handlers = Array.isArray(data.handlers) ? data.handlers : [];
            const approvalProcess = serviceCode?.approvalProcess;

            setServiceRequest({
                ...data,
                serviceCode: serviceCode ? {...serviceCode} : null,
                serviceCodeId: serviceCode?.id,
                handlers: [...handlers],
                handlerIds: handlers.map((handler) => handler.id),
                requester: {...userAuth},
                requesterId: userAuth.id,
            });

            if (approvalProcess?.id) {
                dispatch(setApprovalProcesses([approvalProcess]));
            } else {
                clearApprovalProcessState(dispatch);
            }
        }
    }

    const handleSetData = async (serviceCodeId) => {
        await getServiceCode(serviceCodeId, (data) => {
            const handlers = data.handlers;
            if (Array.isArray(handlers) && handlers.length > 0) {
                setServiceRequest((prev) => ({
                    ...prev,
                    handlers: [...handlers],
                    handlerIds: handlers.map((handler) => handler.id),
                }));
            } else {
                setServiceRequest((prev) => ({...prev, handlers: [], handlerIds: []}));
            }
            if (data.approvalProcess?.id) {
                dispatch(setApprovalProcesses([data.approvalProcess]));
            } else {
                clearApprovalProcessState(dispatch);
            }
        });
    }

    const handleHandlersChange = (handlers) => {
        setServiceRequest((prev) => ({
            ...prev,
            handlers,
            handlerIds: handlers.map((handler) => handler.id),
        }));
    };

    const handleHandlerRemove = (removed) => {
        setServiceRequest((prev) => ({
            ...prev,
            handlers: (prev.handlers ?? []).filter((handler) => handler.id !== removed.id),
            handlerIds: (prev.handlerIds ?? []).filter((handlerId) => handlerId !== removed.id),
        }));
    };

    const completePreRegister = useCallback(async () => {
        const currentRequest = serviceRequestRef.current;
        const currentDocs = docsRef.current;
        const approvalProcess = approvalProcessRef.current;
        const approvalProcessId = approvalProcess?.id;

        const validationData = {
            serviceCodeId: currentRequest.serviceCodeId,
            requesterId: currentRequest.requesterId,
            handlerIds: currentRequest.handlerIds,
        };
        if (!validation(validationData, t)) {
            return;
        }

        const data = {
            id: currentRequest.id,
            handlerIds: currentRequest.handlerIds,
            requesterId: approvalProcessId,
            serviceCodeId: currentRequest.serviceCodeId,
            approvalProcessId,
            approvalProcessName: approvalProcess?.name,
        };
        await completePreRegisterWork(data, currentDocs, () => {
            handleActiveToast({message: "접수가 등록되었습니다."});
            dispatch(changeRefreshContentStatus());
        });
    }, [dispatch, t]);

    const rollup = useMemo(() => ({
        create: completePreRegister,
        cancel: {url: "/serviceRequest/serviceRequest"},
        data: serviceRequest,
        createTitle: "접수완료",
    }), [completePreRegister, serviceRequest]);

    const requestInfoPanel = (
        <>
            <DetailPanel title={"기본정보"}>
                <DetailKvTable>
                    <tbody>
                    <tr>
                        <Th_title title={"요청제목"} className="fieldset-td" colspan={3}>
                            <ReadOnlyInputBasic value={serviceRequest.title} />
                        </Th_title>
                    </tr>
                    <tr className="asset-detail-kv-row--auto">
                        <Th_title title={"요청내용"} className="fieldset-td" colspan={3}>
                            <div className={'align-text-div'}>{serviceRequest.description}</div>
                        </Th_title>
                    </tr>
                    <tr>
                        <Th_title title={"등록일자"} className="fieldset-td" colspan={1}>
                            <ReadOnlyInputBasic
                                target={"createdAt"}
                                data={{data: serviceRequest, setData: setServiceRequest}}
                                value={serviceRequest.createdAt}
                            />
                        </Th_title>
                        <Th_title title={"수정일자"} className="fieldset-td" colspan={1}>
                            <ReadOnlyInputBasic
                                target={"updatedAt"}
                                data={{data: serviceRequest, setData: setServiceRequest}}
                                value={serviceRequest.updatedAt}
                            />
                        </Th_title>
                    </tr>
                    <tr>
                        <Th_title title={"등록자"} className="fieldset-td" colspan={1}>
                            <ReadOnlyInputBasic
                                target={"createdBy"}
                                data={{data: serviceRequest, setData: setServiceRequest}}
                                value={serviceRequest.createdBy?.name}
                            />
                        </Th_title>
                        <Th_title title={"수정자"} className="fieldset-td" colspan={1}>
                            <ReadOnlyInputBasic
                                target={"updatedBy"}
                                data={{data: serviceRequest, setData: setServiceRequest}}
                                value={serviceRequest.updatedBy?.name}
                            />
                        </Th_title>
                    </tr>
                    <tr>
                        <Th_title title={"요청자"} className="fieldset-td" colspan={1}>
                            <ReadOnlyInputBasic
                                target={"applier"}
                                data={{data: serviceRequest, setData: setServiceRequest}}
                                value={serviceRequest.applier?.name}
                            />
                        </Th_title>
                        <Th_title title={"접수자"} className="fieldset-td" colspan={1} required>
                            <Selector
                                onChange={(value)=> onCreateIdClickListener(
                                    setServiceRequest, UserType.requester, UserIdType.requesterId, serviceRequest.requester, value
                                )}
                                data={serviceRequest.requester}
                                uniqueKey={UserType.requester}
                                entity={"userAccount"} auth>
                                <SimmsInputSearch type={'TEXT'} placeHolder={"사용자명"} />
                                <SimmsDrawerSearch showLabel title={'부서'} entity={'department'} target={'department'} recursive />
                                <SimmsDrawerSearch showLabel title={'업체'} entity={'company'} target={'company'} />
                            </Selector>
                        </Th_title>
                    </tr>
                    <tr>
                        <Th_title title={"요청마감일"} className="fieldset-td" colspan={1}>
                            <ReadOnlyInputBasic
                                target={"requestCloseDate"}
                                data={{data: serviceRequest, setData: setServiceRequest}}
                                value={serviceRequest.requestCloseDate}
                            />
                        </Th_title>
                        <Th_title title={"일련번호"} className="fieldset-td" colspan={1}>
                            <ReadOnlyInputBasic
                                target={"serialNumber"}
                                data={{data: serviceRequest, setData: setServiceRequest}}
                                value={serviceRequest.serialNumber}
                            />
                        </Th_title>
                    </tr>
                    <tr>
                        <Th_title title={"요청코드"} className="fieldset-td" colspan={3}>
                            <Selector
                                onChange={(value)=> onCreateIdClickListener(
                                    setServiceRequest, CodeType.serviceCode, CodeIdType.serviceCodeId,
                                    serviceRequest.serviceCode, value
                                )}
                                data={serviceRequest.serviceCode}
                                entity={"serviceCode"}
                                uniqueKey={CodeType.serviceCode}
                                recursive auth
                            />
                        </Th_title>
                    </tr>
                    </tbody>
                </DetailKvTable>
            </DetailPanel>
        </>
    );

    const workflowPanels = (
        <>
            <DetailPanel title={"처리담당자"}>
                <DetailKvTable>
                    <tbody>
                    <tr className="asset-detail-kv-row--auto">
                        <Th_title title={"처리담당자"} colspan={3} className="fieldset-td fieldset-td-input relative align-top" required>
                            <UserSelectionField
                                layout="inline"
                                data={serviceRequest.handlers ?? []}
                                onRemove={handleHandlerRemove}
                            >
                                <SearchSelector
                                    auth
                                    multiSelect
                                    variant="compact"
                                    entity="userAccount"
                                    uniqueKey={UserType.handlers}
                                    data={serviceRequest.handlers ?? []}
                                    placeHolder="사용자명"
                                    onChange={(value) => {
                                        if (value?.__multiFlat) {
                                            handleHandlersChange(value.__multiFlat);
                                        }
                                    }}
                                >
                                    <SimmsInputSearch type="TEXT" placeHolder="사용자명" />
                                    <SimmsDrawerSearch showLabel title="부서" entity="department" target="department" recursive />
                                    <SimmsDrawerSearch showLabel title="업체" entity="company" target="company" />
                                </SearchSelector>
                            </UserSelectionField>
                        </Th_title>
                    </tr>
                    </tbody>
                </DetailKvTable>
            </DetailPanel>
            <ApprovalComponent
                auth
                contentId={serviceRequest.id}
                contentCategory={ContentCategory.SERVICE_REQUEST}
                onApprovalProcessSet={handleApprovalProcessSet}
            />
        </>
    );

    const asidePanel = (
        <>
            {serviceRequest.serviceApplyId && (
                <DetailFilePanel
                    title="서비스신청 파일"
                    isEditing={false}
                    tableInfo={{
                        tableName: ContentCategory.SERVICE_APPLY,
                        joinKey: serviceRequest.serviceApplyId,
                    }}
                />
            )}
            <DetailFilePanel
                title="첨부"
                pendingFiles={{files: docs, setFiles: setDocs}}
                pendingInputId="service_request_pre_register_doc"
            />
        </>
    );

    return (
        <>
            {Object.keys(serviceRequest).length > 0 && (
                <ContentDetailInfoShell
                    title={serviceRequest.title || "사전등록"}
                    meta={serviceRequest.serialNumber}
                    contentId={serviceRequest.id}
                    contentCategory={ContentCategory.SERVICE_REQUEST}
                    scrollMode="columns"
                    listUrl="/serviceRequest/serviceRequest"
                    rollup={rollup}
                >
                    <ContentDetailThreeColLayout
                        primary={requestInfoPanel}
                        secondary={workflowPanels}
                        aside={asidePanel}
                    />
                </ContentDetailInfoShell>
            )}
        </>
    );
};

export default ServiceRequestPreRegisterInfo;
